// ------------------------------------------------------------
// @project Pagali - Plataforma de Pagamentos
// @file OpenBanking.tsx
// @author Arlinda Peixoto
// @company Pagali
// @description Open Banking — Monitorização de consentimentos AIS/PIS
// @date 2026-04-22
// ------------------------------------------------------------

import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Card, CardContent, Grid2 as Grid,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper,
  Chip, IconButton, Stack, Alert, CircularProgress, Tooltip,
  TextField, MenuItem, Select, FormControl, InputLabel, Button,
  Dialog, DialogTitle, DialogContent, DialogActions, InputAdornment,
} from '@mui/material';
import {
  Refresh, Search, AccountBalance, Link, LinkOff,
  CheckCircle, HourglassTop, Cancel, People, Dns,
  VerifiedUser, FilterList,
} from '@mui/icons-material';
import api from '../../services/api';

// ── Types ────────────────────────────────────────────────────

interface ConsentStats {
  total: number;
  authorized: number;
  pending: number;
  revoked: number;
  uniqueClients: number;
  uniqueBanks: number;
}

interface Consent {
  consentId: string;
  status: 'PENDING' | 'AUTHORIZED' | 'REVOKED' | 'REJECTED' | 'EXPIRED';
  permissions: string[];
  bankCode: string;
  bankName: string;
  clientName: string;
  clientEmail: string;
  authorizedAt: string | null;
  expiresAt: string | null;
  createdAt: string;
}

const STATUS_COLOR: Record<string, 'default' | 'warning' | 'success' | 'error' | 'info'> = {
  PENDING: 'warning',
  AUTHORIZED: 'success',
  REVOKED: 'error',
  REJECTED: 'error',
  EXPIRED: 'default',
};

const STATUS_LABEL: Record<string, string> = {
  PENDING: 'Pendente',
  AUTHORIZED: 'Autorizado',
  REVOKED: 'Revogado',
  REJECTED: 'Rejeitado',
  EXPIRED: 'Expirado',
};

const PERMISSION_LABEL: Record<string, string> = {
  ACCOUNTS_READ: 'Contas',
  BALANCES_READ: 'Saldos',
  TRANSACTIONS_READ: 'Movimentos',
  PAYMENTS_WRITE: 'Pagamentos',
};

// ── Component ────────────────────────────────────────────────

export default function OpenBanking() {
  const [consents, setConsents] = useState<Consent[]>([]);
  const [stats, setStats] = useState<ConsentStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [bankFilter, setBankFilter] = useState('');
  const [revokeTarget, setRevokeTarget] = useState<Consent | null>(null);
  const [revoking, setRevoking] = useState(false);

  const fetchConsents = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params: Record<string, string> = {};
      if (statusFilter) params.status = statusFilter;
      if (bankFilter) params.bankCode = bankFilter;

      const res = await api.get('/client/banking/admin/consents', { params });
      const data = res.data?.data || [];
      setConsents(data);
      setStats(res.data?.stats || null);
    } catch (err: any) {
      setError(err.message || 'Erro ao carregar consentimentos');
    } finally {
      setLoading(false);
    }
  }, [statusFilter, bankFilter]);

  useEffect(() => { fetchConsents(); }, [fetchConsents]);

  const handleRevoke = async () => {
    if (!revokeTarget) return;
    setRevoking(true);
    try {
      await api.delete(`/client/banking/consent/${revokeTarget.consentId}`);
      setRevokeTarget(null);
      fetchConsents();
    } catch (err: any) {
      setError(err.message || 'Erro ao revogar consentimento');
    } finally {
      setRevoking(false);
    }
  };

  const filtered = consents.filter(c =>
    search === '' ||
    c.clientName.toLowerCase().includes(search.toLowerCase()) ||
    c.clientEmail.toLowerCase().includes(search.toLowerCase()) ||
    c.consentId.toLowerCase().includes(search.toLowerCase())
  );

  const uniqueBanks = [...new Set(consents.map(c => c.bankCode))];

  const fmt = (d: string | null) =>
    d ? new Date(d).toLocaleDateString('pt-PT', { timeZone: 'Atlantic/Cape_Verde', day: '2-digit', month: 'short', year: 'numeric' }) : '—';

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={3}>
        <Box>
          <Typography variant="h5" fontWeight={700}>Open Banking</Typography>
          <Typography variant="body2" color="text.secondary">
            Consentimentos AIS/PIS — vinculação de contas bancárias
          </Typography>
        </Box>
        <Button variant="outlined" startIcon={<Refresh />} onClick={fetchConsents} disabled={loading}>
          Atualizar
        </Button>
      </Stack>

      {error && <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>{error}</Alert>}

      {/* Stats */}
      {stats && (
        <Grid container spacing={2} mb={3}>
          {[
            { label: 'Total consentimentos', value: stats.total, icon: <Dns />, color: 'primary.light' },
            { label: 'Autorizados', value: stats.authorized, icon: <CheckCircle color="success" />, color: 'success.light' },
            { label: 'Pendentes', value: stats.pending, icon: <HourglassTop color="warning" />, color: 'warning.light' },
            { label: 'Clientes únicos', value: stats.uniqueClients, icon: <People color="info" />, color: 'info.light' },
            { label: 'Bancos integrados', value: stats.uniqueBanks, icon: <AccountBalance />, color: 'secondary.light' },
            { label: 'Revogados', value: stats.revoked, icon: <Cancel color="error" />, color: 'error.light' },
          ].map(s => (
            <Grid size={{ xs: 12, sm: 6, md: 4, lg: 2 }} key={s.label}>
              <Card variant="outlined">
                <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: '12px !important' }}>
                  <Box sx={{ bgcolor: s.color, borderRadius: 1.5, p: 0.75, display: 'flex' }}>{s.icon}</Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary" display="block">{s.label}</Typography>
                    <Typography variant="h6" fontWeight={700} lineHeight={1.2}>{s.value}</Typography>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Bancos com consentimentos autorizados */}
      {uniqueBanks.length > 0 && (
        <Card variant="outlined" sx={{ mb: 3 }}>
          <CardContent>
            <Stack direction="row" alignItems="center" spacing={1} mb={1.5}>
              <Link fontSize="small" color="action" />
              <Typography variant="subtitle2" fontWeight={600}>Bancos com contas vinculadas</Typography>
            </Stack>
            <Stack direction="row" spacing={1} flexWrap="wrap" gap={1}>
              {uniqueBanks.map(bank => {
                const count = consents.filter(c => c.bankCode === bank && c.status === 'AUTHORIZED').length;
                return (
                  <Chip
                    key={bank}
                    icon={<AccountBalance />}
                    label={`${bank} — ${count} conta${count !== 1 ? 's' : ''}`}
                    color={count > 0 ? 'primary' : 'default'}
                    variant={count > 0 ? 'filled' : 'outlined'}
                    size="small"
                  />
                );
              })}
            </Stack>
          </CardContent>
        </Card>
      )}

      {/* Filtros */}
      <Stack direction="row" spacing={2} mb={2} flexWrap="wrap">
        <TextField
          size="small"
          placeholder="Pesquisar cliente, email, ID..."
          value={search}
          onChange={e => setSearch(e.target.value)}
          InputProps={{ startAdornment: <InputAdornment position="start"><Search fontSize="small" /></InputAdornment> }}
          sx={{ width: 280 }}
        />
        <FormControl size="small" sx={{ minWidth: 140 }}>
          <InputLabel>Estado</InputLabel>
          <Select value={statusFilter} label="Estado" onChange={e => setStatusFilter(e.target.value)}>
            <MenuItem value="">Todos</MenuItem>
            {['AUTHORIZED', 'PENDING', 'REVOKED', 'REJECTED', 'EXPIRED'].map(s => (
              <MenuItem key={s} value={s}>{STATUS_LABEL[s]}</MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 140 }}>
          <InputLabel>Banco</InputLabel>
          <Select value={bankFilter} label="Banco" onChange={e => setBankFilter(e.target.value)}>
            <MenuItem value="">Todos</MenuItem>
            {uniqueBanks.map(b => <MenuItem key={b} value={b}>{b}</MenuItem>)}
          </Select>
        </FormControl>
        <IconButton onClick={fetchConsents} disabled={loading}><Refresh /></IconButton>
      </Stack>

      {/* Tabela */}
      {loading ? (
        <Box display="flex" justifyContent="center" py={6}><CircularProgress /></Box>
      ) : (
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: 'action.hover' }}>
                <TableCell><strong>ID Consentimento</strong></TableCell>
                <TableCell><strong>Cliente</strong></TableCell>
                <TableCell><strong>Banco</strong></TableCell>
                <TableCell><strong>Permissões</strong></TableCell>
                <TableCell><strong>Estado</strong></TableCell>
                <TableCell><strong>Autorizado em</strong></TableCell>
                <TableCell><strong>Expira em</strong></TableCell>
                <TableCell align="center"><strong>Ações</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filtered.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                    Nenhum consentimento encontrado.
                  </TableCell>
                </TableRow>
              ) : filtered.map(c => (
                <TableRow key={c.consentId} hover>
                  <TableCell sx={{ fontFamily: 'monospace', fontSize: 11 }}>
                    {c.consentId.substring(0, 16)}…
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" fontWeight={500}>{c.clientName}</Typography>
                    <Typography variant="caption" color="text.secondary">{c.clientEmail}</Typography>
                  </TableCell>
                  <TableCell>
                    <Chip icon={<AccountBalance />} label={c.bankName} size="small" variant="outlined" />
                  </TableCell>
                  <TableCell>
                    <Stack direction="row" spacing={0.5} flexWrap="wrap" gap={0.5}>
                      {c.permissions.map(p => (
                        <Chip key={p} label={PERMISSION_LABEL[p] || p} size="small"
                          color={p === 'PAYMENTS_WRITE' ? 'secondary' : 'default'} sx={{ fontSize: 10 }} />
                      ))}
                    </Stack>
                  </TableCell>
                  <TableCell>
                    <Chip label={STATUS_LABEL[c.status] || c.status} size="small"
                      color={STATUS_COLOR[c.status]} sx={{ fontSize: 11 }} />
                  </TableCell>
                  <TableCell sx={{ fontSize: 12 }}>{fmt(c.authorizedAt)}</TableCell>
                  <TableCell sx={{ fontSize: 12 }}>{fmt(c.expiresAt)}</TableCell>
                  <TableCell align="center">
                    {c.status === 'AUTHORIZED' && (
                      <Tooltip title="Revogar consentimento">
                        <IconButton size="small" color="error" onClick={() => setRevokeTarget(c)}>
                          <LinkOff fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Dialog: Confirmar revogação */}
      <Dialog open={!!revokeTarget} onClose={() => setRevokeTarget(null)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <LinkOff color="error" /> Revogar consentimento
        </DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            A conta bancária do cliente ficará desvinculada imediatamente.
          </Alert>
          <Typography variant="body2">
            Tem a certeza que pretende revogar o consentimento do cliente{' '}
            <strong>{revokeTarget?.clientName}</strong> com o banco{' '}
            <strong>{revokeTarget?.bankName}</strong>?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRevokeTarget(null)} disabled={revoking}>Cancelar</Button>
          <Button variant="contained" color="error" onClick={handleRevoke} disabled={revoking}
            startIcon={revoking ? <CircularProgress size={16} color="inherit" /> : <LinkOff />}>
            {revoking ? 'A revogar...' : 'Revogar'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
